import { useAuth } from '@/hooks/useAuth'
import { useOffersModule } from '@/hooks/useOffersModule'
import { creatableObjects, type CreatableObject } from '@/lib/roles'

/**
 * What the dock's «+» creates, read once for every surface that has to agree with it.
 *
 * `profiles.account_type` is a SHORTCUT preference («Din opprettingssnarvei»), not a permission:
 * any signed-in account may create an event (RLS `Users can create events` checks only
 * `owner_id = auth.uid()`, measured 30.10.2026). So a page that hides its own create button
 * below `lg` because «the dock has one» must ask THIS hook whether the dock's «+» makes that
 * object, or a vendor's phone has no way to create an event at all while the desktop does.
 */
export function useCreatableObjects(): { canCreate: readonly CreatableObject[]; likely: readonly CreatableObject[]; loading: boolean } {
  const { profile } = useAuth()
  const { enabled: offersEnabled, loading } = useOffersModule()
  const accountType = (profile as { account_type?: string } | null)?.account_type
  // Until the organisation is known, offer nothing that might be switched off.
  const canCreate = creatableObjects(accountType, { offersEnabled: offersEnabled && !loading })
  // For LAYOUT only: the answer once the module read lands, assuming the usual (modules on).
  // A page that reserves space for a create button decides on this, so the button does not
  // appear a beat after the page (Pilot 30.10.2026: one layout shift for vendors on /events).
  const likely = loading ? creatableObjects(accountType) : canCreate
  return { canCreate, likely, loading }
}
